import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { showError, showSuccess } from '../../services/toaster';
import {
  cancelAlyvePolicy,
  createAlyvePolicySso,
  endorseAlyveMembers,
  fetchAlyvePolicy,
} from '../../services/alyve';
import { AlyveMemberAction, AlyvePolicy, AlyvePolicyMember } from '../../types/alyve';

const initialMemberForm: AlyvePolicyMember & { action: AlyveMemberAction } = {
  name: '',
  gender: 'male',
  dob: '',
  relation: 'Self',
  externalRefId: '',
  status: 'active',
  action: 'add',
};

function cleanPayload<T extends Record<string, any>>(payload: T): Partial<T> {
  return Object.keys(payload).reduce((acc, key) => {
    const value = payload[key];
    if (value !== '' && value !== undefined && value !== null) {
      acc[key as keyof T] = value;
    }
    return acc;
  }, {} as Partial<T>);
}

function toIsoDate(value?: string) {
  if (!value) return '';
  if (/^\d{8}$/.test(value)) return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  return value;
}

const AlyveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = React.useState<AlyvePolicy | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = React.useState(false);
  const [memberForm, setMemberForm] = React.useState<AlyvePolicyMember & { action: AlyveMemberAction }>(initialMemberForm);
  const [policyStartDate, setPolicyStartDate] = React.useState('');
  const [policyEndDate, setPolicyEndDate] = React.useState('');

  const loadPolicy = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await fetchAlyvePolicy(id);
      setPolicy(data);
      setPolicyStartDate(toIsoDate(data.policyStartDate));
      setPolicyEndDate(toIsoDate(data.policyEndDate));
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to fetch Alyve policy');
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const runAction = async (action: () => Promise<void>, message: string) => {
    setSubmitting(true);
    try {
      await action();
      await loadPolicy();
      showSuccess(message);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Alyve action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectedSso = async () => {
    if (!policy) return;
    await runAction(async () => {
      const result = await createAlyvePolicySso(policy._id);
      window.open(result.redirectUrl, '_blank', 'noopener,noreferrer');
    }, 'Alyve SSO URL created');
  };

  const handleCancelPolicy = async () => {
    if (!policy || !window.confirm(`Cancel policy ${policy.policyNumber}?`)) return;
    await runAction(async () => {
      await cancelAlyvePolicy(policy._id);
    }, 'Alyve cancellation request submitted');
  };

  const openAddMember = () => {
    setMemberForm(initialMemberForm);
    setMemberDialogOpen(true);
  };

  const loadMemberForEdit = (member: AlyvePolicyMember) => {
    setMemberForm({
      name: member.name,
      gender: member.gender,
      dob: toIsoDate(member.dob),
      relation: member.relation,
      externalRefId: member.externalRefId,
      status: member.status || 'active',
      action: 'update',
    });
    setMemberDialogOpen(true);
  };

  const handleRemoveMember = async (memberToRemove: AlyvePolicyMember) => {
    if (!policy || !window.confirm(`Remove member ${memberToRemove.name}?`)) return;

    const members = (policy.members || []).map((member) =>
      member.externalRefId === memberToRemove.externalRefId
        ? { ...member, action: 'delete' as AlyveMemberAction, status: 'deleted' }
        : member
    );

    await runAction(async () => {
      setPolicy(await endorseAlyveMembers(policy._id, cleanPayload({
        members,
        policyStartDate: policyStartDate || undefined,
        policyEndDate: policyEndDate || undefined,
      }) as { members: AlyvePolicyMember[]; policyStartDate?: string; policyEndDate?: string }));
    }, 'Alyve member removal submitted');
  };

  const memberActionLabel = () => {
    if (memberForm.action === 'delete') return 'Remove Member';
    if (memberForm.action === 'update') return 'Update Member';
    return 'Add Member';
  };

  const handleMemberSubmit = async () => {
    if (!policy) return;
    if (!memberForm.name || !memberForm.gender || !memberForm.dob || !memberForm.relation || !memberForm.externalRefId) {
      showError('Member name, gender, DOB, relation, and external reference are required');
      return;
    }

    const nextMember: AlyvePolicyMember = {
      name: memberForm.name,
      gender: memberForm.gender,
      dob: memberForm.dob,
      relation: memberForm.relation,
      externalRefId: memberForm.externalRefId,
      action: memberForm.action,
      status: memberForm.action === 'delete' ? 'deleted' : 'active',
    };
    const existingMembers = policy.members || [];
    const members = memberForm.action === 'add'
      ? [...existingMembers, nextMember]
      : existingMembers.map((member) =>
          member.externalRefId === nextMember.externalRefId ? { ...member, ...nextMember } : member
        );

    await runAction(async () => {
      setPolicy(await endorseAlyveMembers(policy._id, cleanPayload({
        members,
        policyStartDate: policyStartDate || undefined,
        policyEndDate: policyEndDate || undefined,
      }) as { members: AlyvePolicyMember[]; policyStartDate?: string; policyEndDate?: string }));
      setMemberForm(initialMemberForm);
      setMemberDialogOpen(false);
    }, 'Alyve member endorsement submitted');
  };

  return (
    <Layout appBarTitle="Alyve Policy Detail">
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/alyve')}>Back</Button>
                <Box>
                  <Typography variant="h6">{loading ? 'Loading...' : policy?.policyNumber || 'Alyve policy'}</Typography>
                  <Typography variant="body2" color="text.secondary">{policy?.coiNumber || '-'}</Typography>
                </Box>
              </Stack>
              {policy && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={handleSelectedSso}
                    disabled={submitting}
                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                  >
                    Open Alyve SSO
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelPolicy}
                    disabled={submitting || policy.policyStatus === 'cancelled'}
                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadPolicy}
                    disabled={loading}
                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                  >
                    Refresh
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        {policy && (
          <>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6">Policy Details</Typography>
                  <Chip size="small" label={policy.policyStatus} />
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}><Typography variant="caption">User ID</Typography><Typography>{policy.userId}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption">Corporate ID</Typography><Typography>{policy.corporateId || '-'}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption">Policy type</Typography><Typography>{policy.policyType || '-'}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption">Members</Typography><Typography>{policy.members?.length || 0}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption">Product code</Typography><Typography>{policy.productCode || '-'}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption">Policy code</Typography><Typography>{policy.policyCode || '-'}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption">Plan order</Typography><Typography>{policy.planOrderId || '-'}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption">Refund amount</Typography><Typography>{policy.refundAmount ?? '-'}</Typography></Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6">Members</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(policy.members || []).length ? `${(policy.members || []).length} member${(policy.members || []).length === 1 ? '' : 's'} linked to this policy.` : 'No members have been linked yet.'}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddAlt1Icon />}
                    onClick={openAddMember}
                    disabled={submitting}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, textTransform: 'none', borderRadius: 1.5 }}
                  >
                    Add Member
                  </Button>
                </Stack>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {(policy.members || []).map((member) => (
                    <Grid item xs={12} md={4} key={member.externalRefId}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography fontWeight={600}>{member.name}</Typography>
                            <Chip size="small" label={member.status || 'active'} />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {[member.relation, member.gender, toIsoDate(member.dob)].filter(Boolean).join(' - ')}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => loadMemberForEdit(member)}
                              sx={{ textTransform: 'none' }}
                            >
                              Edit Member
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => handleRemoveMember(member)}
                              disabled={submitting || member.status === 'deleted'}
                              sx={{ textTransform: 'none' }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            <Dialog open={memberDialogOpen} onClose={() => setMemberDialogOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>{memberActionLabel()}</DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                  <Grid item xs={12} md={6}><TextField fullWidth required label="External ref ID" value={memberForm.externalRefId} disabled={memberForm.action === 'update'} onChange={(e) => setMemberForm({ ...memberForm, externalRefId: e.target.value })} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth required label="Name" value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth required label="Gender" value={memberForm.gender} onChange={(e) => setMemberForm({ ...memberForm, gender: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth required label="DOB" placeholder="YYYY-MM-DD" value={memberForm.dob} onChange={(e) => setMemberForm({ ...memberForm, dob: e.target.value })} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth required label="Relation" value={memberForm.relation} onChange={(e) => setMemberForm({ ...memberForm, relation: e.target.value })} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Policy start date" placeholder="YYYY-MM-DD" value={policyStartDate} onChange={(e) => setPolicyStartDate(e.target.value)} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Policy end date" placeholder="YYYY-MM-DD" value={policyEndDate} onChange={(e) => setPolicyEndDate(e.target.value)} /></Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setMemberDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
                <Button variant="contained" onClick={handleMemberSubmit} disabled={submitting} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                  {memberActionLabel()}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Layout>
  );
};

export default AlyveDetail;
